import {
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    useTheme,
} from "@mui/material";
import React, { Suspense, useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import AgentStore from "../Stores/AgentStore";
import { AuthStore } from "stores/AuthStore";
import { observer } from "mobx-react";
import { AgentLogoWithNameFull } from "../Branding/AgentLogoWithNameFull";
import { NewChatButton } from "../AgentDrawerContents";
import { AgentConversation } from "../Conversation/AgentConversation";
import { AgentConversationEmpty } from "../Conversation/AgentConversationEmpty";
import { AgentFullscreenSidebar } from "./AgentFullscreenSidebar";
import { AgentConversationSkeleton } from "../Conversation/AgentConversationSkeleton";
import { useParams } from "react-router";
import { FullscreenChevron } from "./FullscreenChevron";
import { AcxAgentService } from "services/AcxAgentService";
import { ServiceError } from "services/BaseService";
import { useOpenCloseFullscreenAgent } from "../AgentUtils";
import { Close, Settings } from "@mui/icons-material";
import SingleAgentChatStore from "../Stores/SingleAgentChatStore";
import { useCurrentChatStore } from "../Stores/useCurrentChat";
import { AgentState } from "../Models/AgentState";
import { Chat } from "../Models/Chat";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import { AnalysisGoalHeader } from "./AnalysisGoalHeader";
import AcxChip from "components/UI/AcxChip";
import SparklingSvg from "SvgIcons/SparklingSvg";
import { hasKboSettingsOverrides } from "../Models/KboSettingsHelpers";
import {
    preloadSessionSettingsDialog,
    SessionSettingsDialog,
} from "../Conversation/SessionSettingsDialog.lazy";

const AgentFullscreen = observer(() => {
    const { chatId } = useParams<{ chatId?: string }>();
    const agentStore = useStore(AgentStore);
    const [initialLoad, setInitialLoad] = useState(true);
    const messageStore = useStore(MessageStore);
    useEffect(() => {
        // When we load this component/page, we are definitely in fullscreen mode
        agentStore.closeDrawer();
        agentStore.setFullscreen(true);
        agentStore.markKboUpdateSeen();
        if (chatId) {
            agentStore.setCurrentChatId(chatId);
        }

        if (agentStore.routeBeforeFullscreen === "") {
            agentStore.setRouteBeforeFullscreen("/app/");
        }

        return () => {
            agentStore.setFullscreen(false);
            agentStore.setRouteBeforeFullscreen("/app/");
        };
        // We don't want to redo this effect everytime the chat id changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentStore]);

    useEffect(() => {
        if (!initialLoad) return;
        let stale = false;

        async function loadInitialChat() {
            if (!chatId) {
                // If there's no chat id just set to empty
                if (stale) return;
                // A brand-new chat may already be waiting on its first reply
                // before the backend has assigned it an id; keep showing it.
                if (agentStore.currentChatStore.state === AgentState.Idle) {
                    agentStore.setIsEmpty(true);
                }
                agentStore.setCurrentChatId(emptyGUID);
                setInitialLoad(false);
                return;
            }

            // Expanding from the drawer keeps the chat that is already in
            // memory. Reloading it from the backend would replace the chat
            // object mid-stream (orphaning the streamed reply) or before the
            // latest reply has been persisted.
            const existingChatStore = agentStore.allChats.get(chatId);
            if (
                existingChatStore &&
                (existingChatStore.chatLoaded ||
                    existingChatStore.state !== AgentState.Idle)
            ) {
                agentStore.setIsEmpty(false);
                agentStore.setLoadingCurrentChat(false);
                agentStore.markKboFlowChatRead(existingChatStore.chat);
                setInitialLoad(false);
                return;
            }
            const agentService = new AcxAgentService();
            agentStore.setLoadingCurrentChat(true);
            try {
                // Fetch the chat session
                const chatSession = await agentService.getChatById({
                    chatId,
                });
                if (!chatSession) {
                    throw new Error("Chat session not found");
                }
                if (stale) return;
                let chatStore;
                // If the chat store already exists, update it
                if (agentStore.allChats.has(chatId)) {
                    chatStore = agentStore.allChats.get(chatId);
                    chatStore.loadFromChatSession(chatSession);
                } else {
                    // Otherwise make the chat store
                    chatStore = new SingleAgentChatStore({
                        chat: Chat.fromChatSession(chatSession),
                        agentStore: agentStore,
                        rootStore: agentStore.rootStore,
                    });
                    chatStore.setSessionSettings(
                        chatSession.chatSessionSettings,
                    );
                    agentStore.allChats.set(chatId, chatStore);
                }
                // Chat is now loaded, don't need to preload etc later
                chatStore.setChatLoaded(true);
                agentStore.setIsEmpty(false);
                agentStore.markKboFlowChatRead(chatStore.chat);
                window.history.pushState(
                    {},
                    "",
                    `/app/kbo/chats/${chatStore.chat.id}`,
                );
                agentStore.setLoadingCurrentChat(false);
            } catch (e) {
                if (stale) return;
                const cannotAccessChat =
                    (e instanceof ServiceError &&
                        (e.response.status === 403 ||
                            e.errorMessage === "No chat found")) ||
                    (e instanceof Error &&
                        e.message === "Chat session not found");
                if (cannotAccessChat) {
                    messageStore.logInfo(
                        "You do not have access to this chat. Start a new chat instead.",
                    );
                } else {
                    messageStore.logError(
                        "Failed to load chat, starting a new chat instead.",
                    );
                }
                agentStore.newChat();
                window.history.replaceState({}, "", "/app/kbo/chats");
                agentStore.setLoadingCurrentChat(false);
            }
            if (stale) return;
            setInitialLoad(false);
        }
        loadInitialChat();

        return () => {
            stale = true;
        };
    }, [agentStore, chatId, initialLoad, messageStore]);

    useEffect(() => {
        // Whenever the chatID changes, update the agent store
        agentStore.setCurrentChatId(chatId ?? emptyGUID);
        // We don't care about the agentStore changes, we only care about the chatId param changing
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId]);

    const theme = useTheme();
    const authStore = useStore(AuthStore);
    const currentChatStore = useCurrentChatStore();
    const { closeFullscreenAgent } = useOpenCloseFullscreenAgent();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const isUltra = authStore.isUserUltra();
    const hasSessionSettingsOverrides = hasKboSettingsOverrides(
        currentChatStore.sessionSettings,
        agentStore.defaultSessionSettings,
    );
    // The KBo User Agreement and the message after accepting it are shown in the
    // chat, so a new chat needs the chat layout until then.
    const showsEmptyState =
        agentStore.isEmpty &&
        !agentStore.isKboInteractionLocked &&
        !currentChatStore.showsUserAgreementAccepted;

    useEffect(() => {
        if (!isUltra) return;

        const idleWindow = window as Window & {
            requestIdleCallback?: (
                callback: () => void,
                options?: { timeout?: number },
            ) => number;
            cancelIdleCallback?: (handle: number) => void;
        };

        if (idleWindow.requestIdleCallback) {
            const handle = idleWindow.requestIdleCallback(
                preloadSessionSettingsDialog,
                { timeout: 3000 },
            );
            return () => idleWindow.cancelIdleCallback?.(handle);
        }

        const handle = window.setTimeout(preloadSessionSettingsDialog, 750);
        return () => window.clearTimeout(handle);
    }, [isUltra]);

    return (
        <Grid
            sx={{
                height: "calc(100% - 72px)",
                maxHeight: "calc(100% - 72px)",
                width: "100%",
                display: "flex",
                flexDirection: "row",
            }}
        >
            <FullscreenChevron />
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: 2,
                    paddingRight: 0,
                    paddingTop: 1,
                    height: "100%",
                    width: "20%",
                    backgroundColor: theme.palette.lightgray.main,
                }}
            >
                {!initialLoad && (
                    <AgentFullscreenSidebar
                        canAccessKboSettings={authStore.canUserView("View KBo")}
                    />
                )}
            </Grid>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: theme.palette.white.main,
                    height: "100%",
                    width: "80%",
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexShrink: 0,
                        opacity: 1,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Grid
                            sx={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                            onClick={() => {
                                closeFullscreenAgent();
                                agentStore.openDrawer();
                            }}
                            id="kbo-logo-close-fullscreen"
                        >
                            <AgentLogoWithNameFull width="71" height="24" />
                            {agentStore.showKboUpdateAnnouncement && (
                                <AcxChip
                                    size="small"
                                    color="brandBlend"
                                    label="Updated"
                                    id="kbo-fullscreen-updated-tag"
                                    customMaterialIcon={
                                        <SparklingSvg
                                            gradient={false}
                                            width={12}
                                            height={12}
                                        />
                                    }
                                    sx={{
                                        marginBottom: 0,
                                        flexShrink: 0,
                                        "& .MuiChip-icon": {
                                            color: "white.main",
                                        },
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "row",
                                gap: 1,
                            }}
                        >
                            <NewChatButton
                                onClick={() => {
                                    if (agentStore.shouldPaywallOnInteract) {
                                        agentStore.openPaywallDialog();
                                        return;
                                    }
                                    agentStore.newChat();
                                    agentStore.setIsEmpty(true);
                                    window.history.pushState(
                                        {},
                                        "",
                                        "/app/kbo/chats",
                                    );
                                    agentStore.setLoadingCurrentChat(false);
                                }}
                                fullWidth={false}
                                id="kbo-fullscreen-new-chat"
                                variant="outlined"
                                color="white"
                            >
                                New Chat
                            </NewChatButton>
                            {isUltra && (
                                <Tooltip title="Session settings">
                                    <IconButton
                                        onMouseEnter={preloadSessionSettingsDialog}
                                        onFocus={preloadSessionSettingsDialog}
                                        onClick={() => {
                                            preloadSessionSettingsDialog();
                                            setSettingsOpen(true);
                                        }}
                                        id="kbo-fullscreen-session-settings"
                                        aria-label="Session settings"
                                    >
                                        <Settings
                                            sx={{
                                                color: hasSessionSettingsOverrides
                                                    ? theme.palette.primary.main
                                                    : theme.palette.gray[400],
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton
                                onClick={() => {
                                    closeFullscreenAgent();
                                    agentStore.closeDrawer();
                                }}
                                id="kbo-fullscreen-close"
                            >
                                <Close />
                            </IconButton>
                        </Grid>
                    </Stack>
                    {agentStore.currentChatStore.chat.wasKboAnalysis && (
                        <AnalysisGoalHeader />
                    )}
                </DialogTitle>
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: showsEmptyState
                            ? "center"
                            : "space-between",
                        alignItems: "center",
                        flex: 1,
                        minHeight: 0,
                        paddingBottom: "54px",
                    }}
                >
                    {showsEmptyState && !agentStore.loadingCurrentChat && (
                        <AgentConversationEmpty fullscreen />
                    )}
                    {!showsEmptyState && !agentStore.loadingCurrentChat && (
                        <AgentConversation fullscreen />
                    )}
                    {agentStore.loadingCurrentChat && (
                        <AgentConversationSkeleton fullscreen />
                    )}
                </Grid>
            </Grid>
            {isUltra && settingsOpen && (
                <Suspense fallback={null}>
                    <SessionSettingsDialog
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                    />
                </Suspense>
            )}
        </Grid>
    );
});

export default AgentFullscreen;
